import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [profileUser, setProfileUser] = useState(null);
  const [savedIncidents, setSavedIncidents] = useState([]);
  const [assignedIncidents, setAssignedIncidents] = useState([]);
  const [createdIncidents, setCreatedIncidents] = useState([]);
  const [assignedCount, setAssignedCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    location: "",
  });

  const loggedUser = JSON.parse(localStorage.getItem("user"));

  const getRoleLabel = (role) => {
    if (role === "Admin") return "Administrator";
    if (role === "IKT") return "Informatik";
    return "Uporabnik";
  };

  // =========================
  // FETCH HELPERS
  // =========================
  const fetchSaved = async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/getSavedIncidents.php?user_id=${userId}`,
      );
      const data = await res.json();

      const list =
        data.incidents ||
        data.saved ||
        data.data ||
        (Array.isArray(data) ? data : []);

      setSavedIncidents(list);
    } catch (err) {
      console.log("SAVED ERROR:", err);
    }
  };

  const fetchAssigned = async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/getAssignedIncidents.php?user_id=${userId}`,
      );
      const data = await res.json();

      const list = data.incidents || [];
      setAssignedIncidents(list);
      setAssignedCount(list.length);
    } catch (err) {
      console.log("ASSIGNED ERROR:", err);
    }
  };

  const fetchCreated = async (userId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/getCreatedIncidents.php?user_id=${userId}`,
      );
      const data = await res.json();

      setCreatedIncidents(data.incidents || []);
    } catch (err) {
      console.log("CREATED ERROR:", err);
    }
  };

  // =========================
  // LOAD PROFILE
  // =========================
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);

        let user = null;
        let userId = null;

        if (!id) {
          user = loggedUser;
          userId = loggedUser?.id;
        } else {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/getUser.php?id=${id}`,
          );
          const data = await res.json();

          user = Array.isArray(data)
            ? data.find((u) => String(u.id) === String(id))
            : data?.user || data;

          userId = id;
        }

        if (!user) {
          setError("User not found");
          setLoading(false);
          return;
        }

        setProfileUser(user);

        setFormData({
          email: user.email || "",
          phone: user.phone || "",
          location: user.location || "Maribor, Slovenija",
        });

        if (userId) {
          fetchSaved(userId);
          fetchAssigned(userId);
          fetchCreated(userId);
        }
      } catch (err) {
        console.log(err);
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // =========================
  // ROLE CHECK
  // =========================
  const isAdmin = profileUser?.role === "Admin";
  const isIKT = profileUser?.role === "IKT";
  const canSeeAssigned = isAdmin || isIKT;

  // =========================
  // EDIT HANDLERS
  // =========================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const toggleEdit = () => {
    setEditMode(!editMode);
  };

  const saveProfile = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/updateUser.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: profileUser.id,
            email: formData.email,
            phone: formData.phone,
            location: formData.location,
          }),
        },
      );

      const data = await res.json();

      if (data.success) {
        setProfileUser((prev) => ({
          ...prev,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
        }));

        setEditMode(false);
      } else {
        alert("Update failed");
      }
    } catch (err) {
      console.error(err);
      alert("Server error");
    }
  };

  // =========================
  // LOADING / ERROR
  // =========================
  if (loading) return <p className="p-5">Loading profile...</p>;
  if (error) return <p className="p-5 text-red-500">{error}</p>;
  if (!profileUser) return <p className="p-5">No profile data</p>;

  return (
    <div className="bg-gray-50 p-4 rounded-2xl min-h-screen space-y-6">
      {/* HEADER */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row items-center gap-6 border-b pb-6">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#0f2d56] to-[#4f82ab] text-white flex items-center justify-center text-5xl font-bold">
            {profileUser.name?.charAt(0)?.toUpperCase()}
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold">
              {profileUser.name} {profileUser.surname}
            </h2>

            <p className="text-[#4f82ab] font-medium mt-1">
              {getRoleLabel(profileUser.role)}
            </p>

            {canSeeAssigned && (
              <div className="mt-3 p-3 bg-blue-50 rounded-xl">
                <p className="text-sm text-gray-600">Prevzeti incidenti</p>
                <p className="text-xl font-bold text-[#0f2d56]">
                  {assignedCount}
                </p>
              </div>
            )}

            {/* EDIT BUTTONS */}
            <div className="flex gap-2 mt-4">
              {!editMode ? (
                <button
                  onClick={toggleEdit}
                  className="px-5 py-2 rounded-xl bg-[#0f2d56] text-white text-sm"
                >
                  Uredi profil
                </button>
              ) : (
                <>
                  <button
                    onClick={saveProfile}
                    className="px-5 py-2 rounded-xl bg-green-600 text-white text-sm"
                  >
                    Shrani
                  </button>

                  <button
                    onClick={toggleEdit}
                    className="px-5 py-2 rounded-xl bg-gray-300 text-black text-sm"
                  >
                    Prekliči
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* INFO GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* EMAIL */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <p className="text-sm text-gray-500">Email</p>
            {editMode ? (
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full mt-1 p-2 border rounded-lg"
              />
            ) : (
              <p className="font-semibold">{profileUser.email}</p>
            )}
          </div>

          {/* PHONE */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <p className="text-sm text-gray-500">Telefon</p>
            {editMode ? (
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full mt-1 p-2 border rounded-lg"
              />
            ) : (
              <p className="font-semibold">{profileUser.phone || "-"}</p>
            )}
          </div>

          {/* LOCATION */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <p className="text-sm text-gray-500">Lokacija</p>
            {editMode ? (
              <input
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full mt-1 p-2 border rounded-lg"
              />
            ) : (
              <p className="font-semibold">
                {profileUser.location || "Maribor, Slovenija"}
              </p>
            )}
          </div>

          {/* ROLE */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <p className="text-sm text-gray-500">Vloga</p>
            <p className="font-semibold">{getRoleLabel(profileUser.role)}</p>
          </div>
        </div>
      </div>

      {/* SAVED */}
      <Section title="Saved Incidents">
        {savedIncidents.length === 0 ? (
          <Empty text="No saved incidents" />
        ) : (
          savedIncidents.map((inc) => (
            <IncidentCard key={inc.id} inc={inc} navigate={navigate} />
          ))
        )}
      </Section>

      {/* ASSIGNED */}
      {canSeeAssigned && (
        <Section title="Assigned Incidents">
          {assignedIncidents.length === 0 ? (
            <Empty text="No assigned incidents" />
          ) : (
            assignedIncidents.map((inc) => (
              <IncidentCard key={inc.id} inc={inc} navigate={navigate} />
            ))
          )}
        </Section>
      )}

      {/* CREATED */}
      <Section title="Created Incidents">
        {createdIncidents.length === 0 ? (
          <Empty text="No created incidents" />
        ) : (
          createdIncidents.map((inc) => (
            <IncidentCard key={inc.id} inc={inc} navigate={navigate} />
          ))
        )}
      </Section>
    </div>
  );
};

export default Profile;

/* ========================= */
/* SMALL COMPONENTS */
/* ========================= */

const Section = ({ title, children }) => (
  <div className="bg-white p-6 rounded-2xl shadow-sm space-y-3">
    <h3 className="text-lg font-bold text-[#0f2d56]">{title}</h3>
    {children}
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-gray-500">{text}</p>;

const IncidentCard = ({ inc, navigate }) => (
  <div className="p-3 bg-gray-50 rounded-xl flex justify-between items-center">
    <div>
      <p className="font-semibold">INC-{inc.id}</p>
      <p className="text-xs text-gray-500">{inc.title}</p>
    </div>

    <button
      onClick={() => navigate(`/incidents/${inc.id}`)}
      className="text-sm text-[#0f2d56] font-medium"
    >
      Open
    </button>
  </div>
);
